"use client";

import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header } from "../Components";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { InputTextarea } from "primereact/inputtextarea";
import axios from "axios";
import { useBaseUrl } from "../Contexts/BaseUrlContext";

const GroupMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pkgDialogVisible, setPkgDialogVisible] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);
  
  // ✅ Reason Dialog State
  const [reasonDialogVisible, setReasonDialogVisible] = useState(false);
  const [reason, setReason] = useState("");
  const [selectedMemberForAction, setSelectedMemberForAction] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // 'block', 'unblock', 'remove'

  const toast = useRef(null);
  const navigate = useNavigate();
  const { groupId } = useParams();

  const { baseUrl } = useBaseUrl();

  // ✅ axios instance (same as other group pages)
  const api = axios.create({
    baseURL: `${baseUrl}/`,
    withCredentials: true,
    headers: { "Content-Type": "multipart/form-data" },
  });

  // 🔥 Fetch Group Members
  const fetchMembers = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("action", "getGroupMembers");
      formData.append("group_id", groupId);

      const res = await api.post("/group.php", formData);

      setMembers(res.data.members || []);
    } catch (err) {
      console.error("API Error:", err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch group members",
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMembers();
  }, [groupId]);

  // 🔥 Handle Click Actions
  const handleActionClick = (member, action) => {
    setSelectedMemberForAction(member);
    setPendingAction(action);
    setReason("");
    setReasonDialogVisible(true);
  };

  // 🔥 Process Action with Reason
  const processAction = async () => {
    if (!reason.trim()) {
      toast.current.show({
        severity: "warn",
        summary: "Reason Required",
        detail: "Please enter a reason for this action",
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("reason", reason);
      formData.append("index_id", selectedMemberForAction.index_id);

      let endpoint = "/group.php";
      
      if (pendingAction === 'block' || pendingAction === 'unblock') {
        formData.append("action", "updateGroupMemberStatus");
        formData.append("status", pendingAction === 'block' ? "block" : "active");
      } else if (pendingAction === 'remove') {
        formData.append("action", "removeGroupMember");
        formData.append("status", "block");
      }

      const res = await api.post(endpoint, formData);

      if (res.data.error === 0) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: `Member ${pendingAction}ed successfully`,
        });
        setReasonDialogVisible(false);
        fetchMembers();
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: res.data.message || "Action failed",
        });
      }
    } catch (err) {
      console.error(err);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "An error occurred while processing the request",
      });
    }
    setLoading(false);
  };

  // 🔥 Block/Unblock Member
  const toggleBlock = (member) => {
    const isCurrentlyBlocked = member.status === "Block" || member.status === "blocked" || member.status === "block";
    handleActionClick(member, isCurrentlyBlocked ? 'unblock' : 'block');
  };

  // 🔥 Confirm Remove Member
  const confirmRemove = (member) => {
    handleActionClick(member, 'remove');
  };

  // Column: Member Profile
  const profileBody = (rowData) => (
    <div className="flex items-center gap-3">
      <img
        src={rowData.profile_img}
        alt={rowData.member_name}
        className="w-10 h-10 rounded-full object-cover border-2 border-gray-100 shadow-sm"
        onError={(e) => { e.target.src = "data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2240%22%20height%3D%2240%22%3E%3Crect%20width%3D%2240%22%20height%3D%2240%22%20fill%3D%22%23e2e8f0%22%2F%3E%3Ctext%20x%3D%2250%25%22%20y%3D%2250%25%22%20dominant-baseline%3D%22middle%22%20text-anchor%3D%22middle%22%20fill%3D%22%2364748b%22%3E%3F%3C%2Ftext%3E%3C%2Fsvg%3E"; }}
      />
      <div className="flex flex-col">
        <span className="font-semibold text-gray-800 leading-tight">{rowData.member_name}</span>
        <span className="text-xs text-gray-500">ID: {rowData.member_id}</span>
      </div>
    </div>
  );

  // Column: Status Tag
  const statusBody = (rowData) => {
    const status = rowData.status?.toLowerCase();
    const isBlocked = status === "block" || status === "blocked";
    const isActive = status === "active" || status === "live";

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${isBlocked ? 'bg-red-100 text-red-600' : isActive ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
        {rowData.status}
      </span>
    );
  };

  // Column: Package Name (Clickable)
  const pkgBody = (rowData) => (
    <span
      className="text-blue-600 cursor-pointer hover:underline font-medium"
      onClick={() => {
        setSelectedPkg(rowData);
        setPkgDialogVisible(true);
      }}
    >
      {rowData.pkg_name}
    </span>
  );


  // Column: Actions
  const actionBody = (rowData) => {
    const status = rowData.status?.toLowerCase();
    const isBlocked = status === "block" || status === "blocked";
    return (
      <div className="flex gap-2">
        <Button
          label={isBlocked ? "Unblock" : "Block"}
          icon={isBlocked ? "pi pi-check-circle" : "pi pi-ban"}
          className={`${isBlocked ? "p-button-success" : "p-button-warning"} p-button-sm p-button-rounded`}
          onClick={() => toggleBlock(rowData)}
          tooltip={isBlocked ? "Allow member" : "Restrict member"}
        />
        <Button
          label="Remove"
          icon="pi pi-user-minus"
          className="p-button-danger p-button-sm p-button-rounded"
          onClick={() => confirmRemove(rowData)}
          tooltip="Remove from group"
        />
      </div>
    );
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl shadow-xl transition-all duration-300">
      <Header
        category="Group"
        title="Group Members"
      />

      <Toast ref={toast} />
      <ConfirmDialog />

      <Dialog
        header="Package Details"
        visible={pkgDialogVisible}
        style={{ width: '450px' }}
        onHide={() => setPkgDialogVisible(false)}
        className="rounded-xl shadow-2xl"
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
      >
        {selectedPkg && (
          <div className="grid grid-cols-2 gap-4 p-2">
            <div className="flex flex-col border-b pb-2">
              <span className="text-gray-500 text-sm">Package Name</span>
              <span className="font-bold text-gray-800">{selectedPkg.pkg_name}</span>
            </div>
            <div className="flex flex-col border-b pb-2">
              <span className="text-gray-500 text-sm">Price</span>
              <span className="font-bold text-gray-800">₹{selectedPkg.price_start}</span>
            </div>
            <div className="flex flex-col border-b pb-2">
              <span className="text-gray-500 text-sm">Direct Commission</span>
              <span className="font-bold text-gray-800">₹{selectedPkg.direct_comission}</span>
            </div>
            <div className="flex flex-col border-b pb-2">
              <span className="text-gray-500 text-sm">Team Commission</span>
              <span className="font-bold text-gray-800">₹{selectedPkg.team_comission}</span>
            </div>
            <div className="flex flex-col border-b pb-2">
              <span className="text-gray-500 text-sm">Service Discount</span>
              <span className="font-bold text-gray-800">{selectedPkg.service_discount}%</span>
            </div>
            <div className="flex flex-col border-b pb-2">
              <span className="text-gray-500 text-sm">Daily Limit</span>
              <span className="font-bold text-gray-800">{selectedPkg.daily_limit}</span>
            </div>
          </div>
        )}
      </Dialog>
      
      {/* 📝 Reason Dialog */}
      <Dialog
        header={`Reason for ${pendingAction?.toUpperCase()}`}
        visible={reasonDialogVisible}
        style={{ width: '400px' }}
        onHide={() => setReasonDialogVisible(false)}
        footer={(
          <div className="flex justify-end gap-2">
            <Button label="Cancel" className="p-button-text p-button-secondary" onClick={() => setReasonDialogVisible(false)} />
            <Button label="Confirm" className="p-button-danger" onClick={processAction} loading={loading} />
          </div>
        )}
        className="rounded-xl shadow-2xl"
      >
        <div className="flex flex-col gap-2">
          <label htmlFor="reason" className="text-sm text-gray-500">Enter a reason for this action:</label>
          <InputTextarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            autoFocus
            className="w-full border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-red-500"
            placeholder={`Why are you ${pendingAction}ing this member?`}
          />
        </div>
      </Dialog>

      <div className="mt-5">
        <DataTable
          value={members}
          dataKey="index_id"
          paginator
          rows={10}
          loading={loading}
          responsiveLayout="scroll"
          className="p-datatable-customers"
          emptyMessage="No members found"
          rowsPerPageOptions={[10, 25, 50]}
        >
          <Column header="Member" body={profileBody} sortable sortField="member_name" />
          <Column field="member_id" header="Member ID" sortable />
          <Column field="platform" header="Platform" sortable />
          <Column header="Package" body={pkgBody} sortable sortField="pkg_name" />
          <Column field="role" header="Role" sortable />
          <Column field="date_time" header="Joined Date" sortable />
          <Column field="status" header="Status" body={statusBody} sortable />
          <Column header="Actions" body={actionBody} className="text-center" />
        </DataTable>
      </div>
    </div>
  );
};

export default GroupMembers;
